import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setErr(""); setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not reset password. The link may have expired.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F6F8FE] via-white to-[#F6F8FE] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-7">
        {done ? (
          <div className="text-center py-6" data-testid="rp-done">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#E8F5E9] flex items-center justify-center mb-3"><CheckCircle2 className="w-7 h-7 text-[#2E7D32]" /></div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Password updated</h1>
            <p className="text-sm text-brand-inkMuted mt-2">You can now sign in with your new password. Redirecting to login…</p>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-2xl bg-brand-purpleLight/60 flex items-center justify-center mb-3"><Lock className="w-6 h-6 text-brand-purple" /></div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Choose a new password</h1>
            <p className="text-sm text-brand-inkMuted mt-1.5">Use at least 8 characters. Make it something you'll remember.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label className="font-heading">New password</Label>
                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-2xl h-11 mt-1.5" autoFocus data-testid="rp-pw" />
              </div>
              <div>
                <Label className="font-heading">Confirm new password</Label>
                <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="rounded-2xl h-11 mt-1.5" data-testid="rp-pw2" />
              </div>
              {err && (
                <div className="rounded-2xl bg-[#FDECEA] border border-[#F8B4B0] p-3 flex gap-2 text-sm text-[#C62828]" data-testid="rp-err">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> {err}
                </div>
              )}
              <Button type="submit" disabled={busy} className="w-full rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11" data-testid="rp-submit">
                {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Updating…</> : "Update password"}
              </Button>
              <p className="text-xs text-center text-brand-inkMuted"><Link to="/login" className="hover:underline" data-testid="rp-back-link">Back to login</Link></p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
